/* eslint-disable class-methods-use-this */

/**
 * Fade Reveal Module
 * Handles the expand/collapse functionality for the fade reveal block
 */

import breakpoints from '../../scss/base/_breakpoints.module.scss';
import { snakeCaseToTitleCase } from '../utils';

/**
 * FadeRevealModule - Manages expand/collapse functionality for a single fade reveal container
 */
class FadeRevealModule {
  /**
   * Creates a new FadeRevealModule instance
   * @param {HTMLElement} containerElement - The fade reveal container element
   */
  constructor(containerElement) {
    this.$container = jQuery(containerElement);
    this.$row = this.$container.find('.fade-reveal__row');
    this.$notFound = this.$row.parent().find('.card__not_found');
    this.$defaultResults = this.$row.clone();
    this.isTermArchive = this.$container.data('isTermArchive') || false;
    this.postType = this.$container.data('postType');
    this.maxFetch = this.$container.data('maxFetch');
    this.searchEnabled = this.$container.data('searchEnabled');

    this.initializeEventHandlers();
    if (this.isTermArchive) {
      this.updateTermHeaderRoles(this.$container);
      $(window).on('resize', () => this.updateTermHeaderRoles(this.$container));
      this.$container.on('click', 'div.card__img-disabled-term[role="button"]', this.handleTermCardExpansion);
    }
  }

  /**
   * Initializes all event handlers for this module instance
   */
  initializeEventHandlers() {
    this.initializeExpandButtons();
    if (this.searchEnabled) {
      this.initializeSearch();
      this.initializeFilters();
    }
  }

  /**
   * Initializes expand/collapse button handlers
   */
  initializeExpandButtons() {
    // Handle post preview expansion
    this.$container
      .find('.fade-reveal__expand-btn:not(.fade-reveal__expand-btn--text)')
      .on('click', (e) => this.handlePostPreviewExpand(e));

    // Handle text module expansion
    this.$container
      .find('.fade-reveal__expand-btn--text')
      .on('click', (e) => this.handleTextExpand(e));
  }

  /**
   * Initializes search functionality
   */
  initializeSearch() {
    const $searchInput = this.$container.find('input[id$="_fade_reveal_search"]');
    const $searchButton = this.$container.find('.search-trigger');

    if ($searchInput.length) {
      // Trigger search on Enter key
      $searchInput.on('keypress', (e) => {
        if (e.key === 'Enter' || e.keyCode === 13) {
          e.preventDefault();
          this.handleSearch(e);
        }
      });

      // Trigger search on button click
      if ($searchButton.length) {
        $searchButton.on('click', (e) => {
          e.preventDefault();
          $searchInput[0].dispatchEvent(new KeyboardEvent('keypress', {
            key: 'Enter',
            code: '13',
            bubbles: true,
            cancelable: true,
          }));
        });
      }
    }
  }

  /**
   * Initializes filter functionality (select dropdowns and button filters)
   */
  initializeFilters() {
    // Initialize select filters
    const $selectFilters = this.$container.find('select[id^="fade_reveal_filter"]');
    $selectFilters.each((_, element) => {
      element.addEventListener('change', (e) => this.handleCategorySelect(e));
    });

    // Initialize button filters
    const $btnFilters = this.$container.find('button[id^="fade_reveal_filter"]');
    $btnFilters.each((_, btnElement) => {
      const $btn = jQuery(btnElement);
      $btn.on('click', (e) => {
        $btn.toggleClass('active');
        if ($btn.hasClass('active')) {
          this.handleCategorySelect(e);
        } else {
          this.resetToDefaultResults();
        }
      });
    });
  }

  /**
   * Handles post preview expand button clicks
   * @param {Event} e - The click event
   */
  handlePostPreviewExpand(e) {
    e.preventDefault();

    const $button = jQuery(e.currentTarget);
    const archiveUrl = $button.data('archive-url');

    // Check if already expanded
    if (this.$container.hasClass('expanded')) {
      // Second click - navigate to archive
      if (archiveUrl) {
        window.location.href = archiveUrl;
      }
    } else {
      // First click - expand to show all posts
      this.setExpansionState(true);

      // Update button text and add state class
      $button.addClass('fade-reveal__expand-btn--view-all');
      $button.find('span').text('View all');

      const isMobileOrTablet = jQuery(window).width() <= parseInt(breakpoints.tablet, 10);
      let $scrollTarget = this.$row.find('.fade-reveal__col--hidden').first();

      if (this.postType === 'pay_equity_laws' && !isMobileOrTablet) {
        // on desktop, only the second pay equity law gets shown on expand
        $scrollTarget = this.$row.find('.fade-reveal__col').eq(1);
      }

      // Scroll to show revealed content
      if ($scrollTarget.length > 0) {
        this.scrollToReveal($scrollTarget);
      }
    }
  }

  /**
   * Handles text module expand/collapse button clicks
   * @param {Event} e - The click event
   */
  handleTextExpand(e) {
    e.preventDefault();

    const $button = jQuery(e.currentTarget);
    const $content = this.$container.find('.fade-reveal__content');
    const isExpanded = this.$container.hasClass('expanded');

    if (isExpanded) {
      // Collapse the text module
      this.$container.removeClass('expanded');
      $button.find('span').text('See more');

      // Scroll to the top of the module
      setTimeout(() => {
        const scrollTarget = this.$container.offset().top - 100;
        jQuery('html, body').animate({ scrollTop: scrollTarget }, 500);
      }, 100);
    } else {
      // Expand the text module
      this.$container.addClass('expanded');
      $button.find('span').text('See less');

      // Scroll to show revealed content
      this.scrollToReveal($content);
    }
  }

  /**
   * Handles search input
   * @param {Event} e - The input event
   */
  async handleSearch(e) {
    let search = e.target.value;

    // Reset to default results when search is cleared
    if (!search) {
      this.resetToDefaultResults();
      return;
    }

    // Clear other filters when searching
    this.clearOtherFilters(e.target);

    search = search.trim().toLowerCase();

    const {
      taxonomyName,
      termId,
    } = e.target.dataset;

    // Build query parameters
    const queryParams = { per_page: this.maxFetch, search };

    if (taxonomyName && termId) {
      queryParams[taxonomyName] = termId;
    }

    if (this.isTermArchive) {
      // eslint-disable-next-line no-underscore-dangle
      queryParams._embed = 1;
      queryParams.orderby = 'title';
      queryParams.order = 'asc';
    }

    // Fetch and display results
    const results = await this.fetchPosts(queryParams);
    this.displayResults(results);
  }

  /**
   * Handles category select/button filter changes
   * @param {Event} e - The change event
   */
  async handleCategorySelect(e) {
    const termID = e.target.value;

    if (!termID) {
      // Remove active from select instance
      if (e.target.customSelect) {
        e.target.customSelect.opener.classList.remove('active');
      }

      this.resetToDefaultResults();
      return;
    }

    // Clear other filters when using this select/button
    this.clearOtherFilters(e.target);

    const {
      taxonomyName,
    } = e.target.dataset;

    if (e.target.customSelect) {
      e.target.customSelect.opener.classList.add('active');
    }

    // Build query parameters
    const queryParams = { per_page: this.maxFetch, [taxonomyName]: termID };

    if (this.isTermArchive) {
      // eslint-disable-next-line no-underscore-dangle
      queryParams._embed = 1;
    }

    // Fetch and display results
    const results = await this.fetchPosts(queryParams);
    this.displayResults(results);
  }

  /**
   * Sets the expansion state of the container
   * @param {boolean} expanded - Whether to expand or collapse
   */
  setExpansionState(expanded) {
    const action = expanded ? 'addClass' : 'removeClass';
    this.$row[action]('fade-reveal__row--expanded');
    this.$container[action]('expanded');
  }

  /**
   * Updates the expansion state based on the number of items
   * Also manages button visibility for search results
   * @param {number} itemCount - Number of items in the row
   */
  updateExpansionState(itemCount) {
    const shouldExpand = itemCount <= 6;
    this.setExpansionState(shouldExpand);

    // Hide/show the expand button based on item count
    this.$container.find('.fade-reveal__expand')[shouldExpand ? 'hide' : 'show']();
  }

  /**
   * Resets the fade reveal to show default results
   */
  resetToDefaultResults() {
    const $newRow = this.$defaultResults.clone();
    this.$row.replaceWith($newRow);
    this.$row = $newRow;
    this.$notFound.hide();
    this.$row.show();
    this.updateExpansionState($newRow.children().length);
  }

  /**
   * Displays filtered results or "not found" message
   * @param {Array} results - The filtered results
   */
  displayResults(results) {
    // Clear current results
    this.$row.empty();

    if (results.length) {
      // Populate row with filtered results
      const $template = this.$defaultResults.find('.fade-reveal__col').first();
      results.forEach((result, index) => {
        const $newCard = this.createCard($template, result, index);
        this.$row.append($newCard);
        if ($newCard.index() > 5) {
          $newCard.addClass('fade-reveal__col--hidden');
        }
      });

      this.$notFound.hide();
      this.$row.show();
      this.updateExpansionState(this.$row.children().length);
      if (this.isTermArchive) {
        this.updateTermHeaderRoles();
      }
    } else {
      // Show "not found" message when no results
      this.setExpansionState(true);
      this.$container.find('.fade-reveal__expand').hide();
      this.$notFound.show();
      this.$row.hide();
    }
  }

  /**
   * Creates a card element from search result data
   * @param {jQuery} $template - The template card to clone
   * @param {Object} result - The search result data
   * @returns {jQuery} The populated card element
   */
  createCard($template, result) {
    if (this.postType === 'pay_equity_laws') {
      return this.createPostEquityCard($template, result);
    }

    const $newCard = $template.clone();
    $newCard.find('h3.post-title').html(result.title.rendered);

    // Point the "Read more" link at the actual result, not the cloned template's post
    $newCard.find('.card__more a').attr('href', result.link);

    // Set excerpt if excerpts are to be shown for this module instance
    const $postExcerptContainer = $newCard.find('div.post-excerpt');
    if ($postExcerptContainer.length) {
      $postExcerptContainer.html(result.excerpt.rendered);
    }

    // Set featured image if images are to be shown for this module instance
    const $imgWrapper = $newCard.find('.card__img-wrapper');
    if ($imgWrapper.length) {
      const $img = jQuery(result.featured_image_thumbnail).addClass('card__img');
      $imgWrapper.empty().append($img);
    }

    return $newCard;
  }

  /**
   * Creates a pay equity card element from search result data
   * @param {jQuery} $template - The template card to clone
   * @param {Object} result - The search result data
   * @returns {jQuery} The populated card element
   */
  createPostEquityCard($template, result) {
    const [stateID] = result.states;
    // eslint-disable-next-line no-underscore-dangle
    const stateTerms = result._embedded['wp:term'].find((f) => f[0].taxonomy === 'states');
    const stateTerm = stateTerms.find((f) => f.id === stateID);
    const $existingStateCard = jQuery(`.fade-reveal__col[data-state-id="${stateID}"]`);
    const $newCard = $existingStateCard.length ? $existingStateCard.first() : $template.clone();
    const $tableWrapper = $newCard.find('div.filter-state__hold');

    if (!$existingStateCard.length) {
      $newCard.find('h3.post-title').html(stateTerm.name);
      $newCard.attr('data-state-id', stateID);
      $tableWrapper.empty();
    }

    const $newStateTable = jQuery('<div class="filter-state__table"></div>');
    Object.entries(result.acf).forEach(([key, value]) => {
      const label = snakeCaseToTitleCase(key);
      const $tableRow = jQuery('<div class="filter-state__table-row"></div>');
      [label, value].forEach((text) => {
        $tableRow.append(`<div class="filter-state__table-td">
          <p>${text.replace(/\r?\n/g, '<br />')}</p>
        </div>`);
      });
      $newStateTable.append($tableRow);
    });
    $tableWrapper.append($newStateTable);

    return $newCard;
  }

  /**
   * Clears all other filters when one filter is activated
   * Ensures only one filter method is active at a time
   * @param {HTMLElement} activeFilterElement - The active filter element (input, select, or button)
   */
  clearOtherFilters(activeFilterElement) {
    // Store the active filter element on the container
    this.$container.data('activeFilter', activeFilterElement);

    // Clear search input if it's not the active filter
    const $searchInput = this.$container.find('input[id$="_fade_reveal_search"]');
    if ($searchInput.length && $searchInput[0] !== activeFilterElement) {
      $searchInput.val('');
    }

    // Clear all select dropdowns except the active one
    this.$container.find('select[id^="fade_reveal_filter"]').each(function clearSelectDropdowns() {
      if (this !== activeFilterElement && this.customSelect) {
        this.customSelect.opener.classList.remove('active');
        this.customSelect.value = '';
      }
    });

    // Clear all button filters except the active one
    this.$container.find('button[id^="fade_reveal_filter"]').each(function clearButtonFilters() {
      if (this !== activeFilterElement) {
        jQuery(this).removeClass('active');
      }
    });
  }

  /**
   * Fetches posts from WordPress API with given parameters
   * @param {Object} queryParams - Additional query parameters
   * @returns {Promise<Array>} The fetched results
   */
  async fetchPosts(queryParams) {
    // wp.api collection names capitalize the first letter of the post type name
    const collectionName = `${this.postType.charAt(0).toUpperCase()}${this.postType.substring(1)}`;
    // eslint-disable-next-line no-undef
    const collection = new wp.api.collections[collectionName]();
    return collection.fetch({ data: { ...queryParams, show_in_fade_reveal: true } });
  }

  /**
   * Animates scroll to reveal hidden content
   * @param {jQuery} $target - The element to scroll to
   * @param {number} offset - Offset from top in pixels
   */
  scrollToReveal($target, offset = 150) {
    if (!$target || !$target.length) return;

    setTimeout(() => {
      const scrollTarget = $target.offset().top - offset;
      jQuery('html, body').animate({ scrollTop: scrollTarget }, 500);
    }, 100);
  }

  /**
   * Updates the role attribute on the modules that loop over terms
   * and posts within the terms based on viewport size
   * On mobile/tablet viewports (<=tablet breakpoint), elements get role="button"
   * On desktop viewports, the role attribute is removed
   * @param {jQuery} $container - The root container instance
   */
  updateTermHeaderRoles($container) {
    const $termHeaders = $container.find('div.card__img-disabled-term');
    const isMobileOrTablet = jQuery(window).width() <= parseInt(breakpoints.tablet, 10);

    if (isMobileOrTablet) {
      $termHeaders.attr('role', 'button');
    } else {
      $termHeaders.removeAttr('role');
      // Clear the style on the card body
      // so that if its display was set to 'none' from the jquery slide toggle,
      // it displays again on desktop
      $container.find('.card__body-term').attr('style', '');
    }
  }

  /**
   * Handles the expansion/collapse of term cards on mobile/tablet viewports
   * Toggles the mobile-expanded class and animates the card body visibility
   * @param {Event} e - The click event
   */
  handleTermCardExpansion(e) {
    const $headerContainer = jQuery(e.currentTarget);
    const $cardBody = $headerContainer.siblings('.card__body-term');

    $headerContainer.toggleClass('mobile-expanded');
    $cardBody.slideToggle(300);
  }
}

/**
 * Initializes all fade reveal modules on the page
 * Creates a new FadeRevealModule instance for each container
 * @param {jQuery} $ - jQuery instance
 */
function initFadeReveal($) {
  const instances = [];

  // Initialize all fade reveal containers
  $('.fade-reveal__post-preview, .fade-reveal__text').each(function initializeContainer() {
    const instance = new FadeRevealModule(this);
    instances.push(instance);
  });

  return instances;
}

jQuery(($) => {
  initFadeReveal($);
});

export default initFadeReveal;
